import React, { Component, useState, useEffect } from 'react';
import LessonLayout from '../components/LessonLayout';
import { Card, Row, Col, Alert, Button, Badge } from 'react-bootstrap';

// Class Component with Lifecycle Methods
class ClassLifecycleComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { count: 0 };
    console.log('🔵 CLASS: constructor() - Component initializing');
  }

  componentDidMount() {
    console.log('🔵 CLASS: componentDidMount() - Component mounted to DOM');
  }

 componentDidUpdate(prevProps, prevState) {
    console.log('🔵 CLASS: componentDidUpdate() - Component updated');
    console.log('   Previous state:', prevState);
    console.log('   Current state:', this.state);
  }

  componentWillUnmount() {
    console.log('🔵 CLASS: componentWillUnmount() - Component will be removed');
  }

  render() {
    console.log('🔵 CLASS: render() - Component rendering');
    return (
      <Card className="mb-3">
        <Card.Header className="bg-primary text-white">Class Component</Card.Header>
        <Card.Body>
          <h4>Count: {this.state.count}</h4>
          <Button onClick={() => this.setState({ count: this.state.count + 1 })} variant="primary">
            Increment
          </Button>
        </Card.Body>
      </Card>
    );
  }
}

// Functional Component with Hooks (Lifecycle Equivalent)
const FunctionalLifecycleComponent = () => {
  const [count, setCount] = useState(0);

  // Equivalent to componentDidMount
  useEffect(() => {
    console.log('🟢 HOOKS: useEffect([]) - Component mounted (like componentDidMount)');
    
    // Equivalent to componentWillUnmount
    return () => {
      console.log('🟢 HOOKS: cleanup - Component unmounting (like componentWillUnmount)');
    };
  }, []);

  // Equivalent to componentDidUpdate
  useEffect(() => {
    console.log('🟢 HOOKS: useEffect([count]) - Component updated (like componentDidUpdate)');
    console.log('   Current count:', count);
  }, [count]);

  // This runs on every render
  useEffect(() => {
    console.log('🟢 HOOKS: useEffect() - Component rendered');
  });

  console.log('🟢 HOOKS: Component function executing (like render)');

  return (
    <Card className="mb-3">
      <Card.Header className="bg-success text-white">Functional Component (Hooks)</Card.Header>
      <Card.Body>
        <h4>Count: {count}</h4>
        <Button onClick={() => setCount(count + 1)} variant="success">
          Increment
        </Button>
      </Card.Body>
    </Card>
  );
};

const Lesson16_Lifecycle = () => {
  const [showClass, setShowClass] = useState(true);
  const [showFunctional, setShowFunctional] = useState(true);

  useEffect(() => {
    console.log('📖 Lesson 16: Component Lifecycle - Loaded');
  }, []);

  return (
    <LessonLayout 
      lessonNumber={16}
      title="Component Lifecycle" 
      description="Understanding component lifecycle in class components vs hooks"
    >
      <Alert variant="success">
        <strong>What You'll Learn:</strong>
        <ul className="mb-0 mt-2">
          <li>Class component lifecycle methods</li>
          <li>Hooks equivalent to lifecycle methods</li>
          <li>Mounting, updating, and unmounting phases</li>
          <li>Side effects in component lifecycle</li>
        </ul>
      </Alert>

      <Row className="mt-4">
        <Col md={6}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Class Component Lifecycle</h5>
            <Button 
              onClick={() => setShowClass(!showClass)} 
              variant={showClass ? 'danger' : 'success'}
              size="sm"
            >
              {showClass ? 'Unmount' : 'Mount'}
            </Button>
          </div>
          {showClass && <ClassLifecycleComponent />}
        </Col>

        <Col md={6}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5>Functional Component (Hooks)</h5>
            <Button 
              onClick={() => setShowFunctional(!showFunctional)} 
              variant={showFunctional ? 'danger' : 'success'}
              size="sm"
            >
              {showFunctional ? 'Unmount' : 'Mount'}
            </Button>
          </div>
          {showFunctional && <FunctionalLifecycleComponent />}
        </Col>
      </Row>

      <h4 className="mt-4">📊 Lifecycle Methods Comparison</h4>
      <Card>
        <Card.Body>
          <table className="table comparison-table">
            <thead>
              <tr>
                <th>Phase</th>
                <th>Class Component</th>
                <th>Hooks Equivalent</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><Badge bg="success">Mounting</Badge></td>
                <td><code>componentDidMount()</code></td>
                <td><code>useEffect(() =&gt; {'{}'}, [])</code></td>
              </tr>
              <tr>
                <td><Badge bg="warning">Updating</Badge></td>
                <td><code>componentDidUpdate()</code></td>
                <td><code>useEffect(() =&gt; {'{}'}, [dep])</code></td>
              </tr>
              <tr>
                <td><Badge bg="danger">Unmounting</Badge></td>
                <td><code>componentWillUnmount()</code></td>
                <td><code>useEffect(() =&gt; return cleanup, [])</code></td>
              </tr>
              <tr>
                <td><Badge bg="info">Every Render</Badge></td>
                <td><code>render()</code></td>
                <td><code>useEffect(() =&gt; {'{}'})</code></td>
              </tr>
            </tbody>
          </table>
        </Card.Body>
      </Card>

      <Card className="mt-3">
        <Card.Header>Lifecycle Code Examples</Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <h6>Class Component</h6>
              <pre className="code-example">
{`class MyComponent extends Component {
  // Mount
  componentDidMount() {
    // Runs once after mount
  }
  
  // Update
  componentDidUpdate(prevProps, prevState) {
    // Runs after every update
  }
  
  // Unmount
  componentWillUnmount() {
    // Cleanup before unmount
  }
  
  render() {
    return <div>Content</div>;
  }
}`}
              </pre>
            </Col>
            <Col md={6}>
              <h6>Functional Component with Hooks</h6>
              <pre className="code-example">
{`function MyComponent() {
  // Mount
  useEffect(() => {
    // Runs once after mount
    
    // Unmount (cleanup)
    return () => {
      // Cleanup before unmount
    };
  }, []);
  
  // Update (when dep changes)
  useEffect(() => {
    // Runs when 'dep' changes
  }, [dep]);
  
  return <div>Content</div>;
}`}
              </pre>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Alert variant="warning" className="mt-4">
        <strong>💡 Key Takeaways:</strong>
        <ul className="mb-0">
          <li>Functional components with hooks are now the recommended approach</li>
          <li>useEffect can replicate all class lifecycle methods</li>
          <li>Cleanup functions in useEffect = componentWillUnmount</li>
          <li>Empty dependency array [] = componentDidMount behavior</li>
          <li>Dependency array [dep] = componentDidUpdate behavior for that dependency</li>
        </ul>
      </Alert>
    </LessonLayout>
  );
};

export default Lesson16_Lifecycle;
